import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Search, Shield, Info, AlertCircle, Loader2, CheckCircle2, Building, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import professionalProfileApi from '../../api/professionalProfile';
import authServiceApi from '../../api/authService';
import Button from '../../components/common/Button';
import { useTheme } from '@mui/material/styles';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

const VisibilityTab = ({ isGloballyHidden, setIsGloballyHidden }) => {
    const [visibilityRules, setVisibilityRules] = useState([]);
    const [allRecruiters, setAllRecruiters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [togglingGlobal, setTogglingGlobal] = useState(false);
    
    // Staging and submitting states
    const [stagedRecruiterIds, setStagedRecruiterIds] = useState([]);
    const [submittingChanges, setSubmittingChanges] = useState(false);

    useEffect(() => {
        fetchInitialData();
    }, []);

    const refreshRules = async () => {
        const rulesRes = await professionalProfileApi.getVisibilityRules();
        setVisibilityRules(rulesRes.data);
    };

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const rulesRes = await professionalProfileApi.getVisibilityRules();
            setVisibilityRules(rulesRes.data);
            
            const recRes = await professionalProfileApi.getRecruitersList('');
            setAllRecruiters(recRes.data.results || []);
        } catch (err) {
            toast.error('Failed to load visibility settings');
        } finally {
            setLoading(false);
        }
    };

    const setVisibilityMode = async (hideFromAll) => {
        if (hideFromAll === isGloballyHidden) return;
        
        const actionText = hideFromAll 
            ? "hide your profile from all recruiters" 
            : "make your profile visible to recruiters (subject to specific rules)";
        
        const confirmResult = await Swal.fire({
            title: hideFromAll ? "Hide Profile Globally?" : "Unhide Profile Globally?",
            text: `Are you sure you want to ${actionText}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#2563eb",
            cancelButtonColor: "#6b7280",
            confirmButtonText: hideFromAll ? "Yes, hide from everyone" : "Yes, make visible"
        });

        if (!confirmResult.isConfirmed) return;

        setTogglingGlobal(true);
        try {
            await professionalProfileApi.updateProfessionalProfile({ is_profile_hidden: hideFromAll });
            setIsGloballyHidden(hideFromAll);
            toast.success(hideFromAll ? 'Profile is now hidden from all recruiters' : 'Profile is now visible, subject to your specific rules');
        } catch (err) {
            toast.error('Failed to update visibility setting');
        } finally {
            setTogglingGlobal(false);
        }
    };

    const updateRule = async (recruiterId, isVisible) => {
        try {
            await professionalProfileApi.upsertVisibilityRule(recruiterId, isVisible);
            await refreshRules();
            toast.success(`Visibility updated for this recruiter`);
        } catch (err) {
            toast.error('Failed to update recruiter visibility');
        }
    };

    const removeRule = async (recruiterId) => {
        try {
            await professionalProfileApi.deleteVisibilityRule(recruiterId);
            await refreshRules();
            toast.success('Recruiter-specific rule removed');
        } catch (err) {
            toast.error('Failed to remove rule');
        }
    };

    const handleUnblockDirect = async (recruiter) => {
        const recruiterId = recruiter.id.toString();
        const name = getRecruiterName(recruiter);

        const confirmResult = await Swal.fire({
            title: "Unhide Profile?",
            text: `Are you sure you want to unhide your profile from ${name}?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#2563eb",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, unblock!"
        });

        if (!confirmResult.isConfirmed) return;

        try {
            await professionalProfileApi.deleteVisibilityRule(recruiterId);
            await refreshRules();
            toast.success(`Profile is now visible to ${name}`);
        } catch (err) {
            toast.error('Failed to unblock recruiter');
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-4" />
                <p className="text-secondary-600 font-medium">Loading your visibility settings...</p>
            </div>
        );
    }

    const hiddenRules = visibilityRules.filter(r => !r.is_visible);
    const hiddenRecruiterIds = hiddenRules.map(r => r.recruiter.id.toString());
    const theme = useTheme();

    const getRecruiterName = (rec) => rec.company_name || rec.full_name || (rec.first_name ? `${rec.first_name} ${rec.last_name || ''}`.trim() : `Recruiter #${rec.id}`);

    const availableRecruiters = allRecruiters.filter(rec => !hiddenRecruiterIds.includes(rec.id.toString()));
    const hasChanges = stagedRecruiterIds.length > 0;

    const handleSubmitSelection = async () => {
        if (stagedRecruiterIds.length === 0) {
            toast.info('No recruiters selected to hide from');
            return;
        }

        const blockNames = stagedRecruiterIds.map(id => {
            const rec = allRecruiters.find(r => r.id.toString() === id.toString());
            return rec ? getRecruiterName(rec) : `ID: ${id}`;
        });

        const confirmResult = await Swal.fire({
            title: "Confirm Hiding Profile?",
            html: `
                <div class="text-left space-y-3">
                    <div>
                        <strong class="text-red-600 block mb-1">Hide Profile From:</strong>
                        <ul class="list-disc pl-5 text-sm text-gray-700">
                            ${blockNames.map(name => `<li>${name}</li>`).join('')}
                        </ul>
                    </div>
                </div>
            `,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#2563eb",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Yes, hide profile!"
        });

        if (!confirmResult.isConfirmed) return;

        setSubmittingChanges(true);
        try {
            for (const id of stagedRecruiterIds) {
                await professionalProfileApi.upsertVisibilityRule(id, false);
            }
            await refreshRules();
            setStagedRecruiterIds([]);
            toast.success('Visibility rules updated successfully');
        } catch (err) {
            toast.error('Failed to update visibility rules');
        } finally {
            setSubmittingChanges(false);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Visibility Mode Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 relative">
                {togglingGlobal && (
                    <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-2xl">
                        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
                    </div>
                )}
                <div 
                    onClick={() => setVisibilityMode(true)}
                    className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${isGloballyHidden ? 'border-primary-500 bg-primary-50/50' : 'border-gray-200 hover:border-primary-300'}`}
                >
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <EyeOff className={isGloballyHidden ? 'text-primary-600' : 'text-gray-400'} size={20} />
                            <h3 className="font-bold text-gray-900 text-lg">Hide from everyone</h3>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${isGloballyHidden ? 'border-primary-500' : 'border-gray-300'}`}>
                            {isGloballyHidden && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                        </div>
                    </div>
                    <p className="text-sm text-gray-500">Your profile will be completely hidden from all recruiter searches and recommendations.</p>
                </div>

                <div 
                    onClick={() => setVisibilityMode(false)}
                    className={`p-5 rounded-2xl border-2 cursor-pointer transition-all ${!isGloballyHidden ? 'border-primary-500 bg-primary-50/50' : 'border-gray-200 hover:border-primary-300'}`}
                >
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Shield className={!isGloballyHidden ? 'text-primary-600' : 'text-gray-400'} size={20} />
                            <h3 className="font-bold text-gray-900 text-lg">Hide for specific recruiters</h3>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${!isGloballyHidden ? 'border-primary-500' : 'border-gray-300'}`}>
                            {!isGloballyHidden && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                        </div>
                    </div>
                    <p className="text-sm text-gray-500">Your profile is visible, but you can block specific recruiters from seeing it.</p>
                </div>
            </div>

            {!isGloballyHidden && (
                <>
                    {/* Dropdown Section */}
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                            <Search size={18} className="text-primary-600" />
                            Hide from Specific Recruiters
                        </h3>
                        <p className="text-sm text-gray-500 mb-6">Select the companies or recruiters you want to block from viewing your profile.</p>

                        <Autocomplete
                            multiple
                            id="recruiter-visibility-search"
                            options={availableRecruiters}
                            value={availableRecruiters.filter(rec => stagedRecruiterIds.includes(rec.id.toString()))}
                            onChange={(event, newValue) => {
                                setStagedRecruiterIds(newValue.map(rec => rec.id.toString()));
                            }}
                            getOptionLabel={(rec) => getRecruiterName(rec)}
                            isOptionEqualToValue={(option, value) => option.id.toString() === value.id.toString()}
                            filterSelectedOptions
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search and select recruiters to hide from..."
                                    variant="outlined"
                                />
                            )}
                        />

                        {/* Submit Button */}
                        <div className="flex justify-end mt-6">
                            <Button
                                variant="primary"
                                disabled={!hasChanges}
                                loading={submittingChanges}
                                onClick={handleSubmitSelection}
                            >
                                Submit Selection
                            </Button>
                        </div>
                    </div>

                    {/* Blacklist Section */}
                    {hiddenRules.length > 0 && (
                        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mt-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <h3 className="font-bold text-gray-900 flex items-center gap-2 mb-2">
                                <EyeOff size={18} className="text-red-500" />
                                Currently Blocked Recruiters ({hiddenRules.length})
                            </h3>
                            <p className="text-sm text-gray-500 mb-6">These recruiters are currently blocked from viewing your profile.</p>
                            
                            <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                                {hiddenRules.map((rule) => {
                                    const rec = rule.recruiter;
                                    return (
                                        <div key={rule.id || rec.id} className="flex items-center justify-between p-4 bg-gray-50/30 hover:bg-gray-50/80 transition-colors">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-500 font-bold border border-red-100">
                                                    {getRecruiterName(rec).charAt(0).toUpperCase()}
                                                </div>
                                                <div>
                                                    <h4 className="font-semibold text-gray-900">{getRecruiterName(rec)}</h4>
                                                    <p className="text-xs text-gray-500">
                                                        Blocked on {new Date(rule.updated_at || rule.created_at).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                onClick={() => handleUnblockDirect(rec)}
                                                className="text-red-600 hover:text-white hover:bg-red-600 border-red-200 hover:border-red-600 transition-all font-medium rounded-lg flex items-center gap-1"
                                            >
                                                Unblock
                                            </Button>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default VisibilityTab;
